import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faMobile } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import MobileAuthModal from '../components/MobileAuthModal';
import '../styles/Auth.css';

const AuthPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [awaitingEmailConfirmation, setAwaitingEmailConfirmation] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  
  const { register, login, currentUser } = useAuth();
  const navigate = useNavigate();

  // Redirect logic when user becomes authenticated
  React.useEffect(() => {
    const handleSuccessfulAuth = async () => {
      console.log('AuthPage useEffect triggered with:', { 
        currentUser: !!currentUser, 
        currentUserRole: currentUser?.role,
        isAdmin: currentUser?.isAdmin
      });
      
      // Check if user is not an admin - redirect to app download page
      if (currentUser && currentUser.isAdmin === false) {
        console.log('AuthPage: Non-admin user detected, redirecting to /get-app');
        navigate('/get-app');
        return;
      }
      
      // Check if there's a redirect URL stored from DirectLinkAuth
      const redirectUrl = localStorage.getItem('redirectAfterAuth');
      if (redirectUrl) {
        console.log('AuthPage: Redirecting to stored URL:', redirectUrl);
        localStorage.removeItem('redirectAfterAuth'); // Clean up
        // Use hash-based navigation for hash router
        if (redirectUrl.startsWith('#')) {
          window.location.hash = redirectUrl.substring(1); // Remove the # and set hash
        } else {
          window.location.href = redirectUrl; // Fallback for full URLs
        }
        return;
      }
      
      console.log('AuthPage: User authenticated, letting HomeRoute handle redirect...');
    };

    if (currentUser) {
      handleSuccessfulAuth();
    }
  }, [currentUser, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    setLoading(true);

    try {
      const result = await login({ email, password, requireAdmin: true });
      
      // Check if user is not an admin and redirect to app download page
      if (result && result.user && result.user.isAdmin === false) {
        navigate('/get-app');
      }
      // If admin, navigation will be handled by AuthContext redirect logic
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred. Please try again.';
      
      // Check if error is about email not confirmed
      if (errorMessage.includes('Email not confirmed') || errorMessage.includes('email_not_confirmed')) {
        setAwaitingEmailConfirmation(true);
        setError('Please check your email and enter the 6-digit confirmation code we sent you.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setVerifyingOtp(true);
    setError('');

    try {
      const { createClient } = await import('@supabase/supabase-js');
      const supabase = createClient(
        'https://kcbpnlgnsdtvpsnlsrnp.supabase.co',
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtjYnBubGduc2R0dnBzbmxzcm5wIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzE0NDA2NzMsImV4cCI6MjA0NzAxNjY3M30.YlrQBZpLGH8OZp-4xxIRiC7gYNBFxKRNc1C5nNZm3zk'
      );

      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: email,
        token: otpCode,
        type: 'signup'
      });

      if (verifyError) {
        throw verifyError;
      }

      // Success - now try to login again
      setAwaitingEmailConfirmation(false);
      setOtpCode('');
      const result = await login({ email, password, requireAdmin: true });
      
      if (result && result.user && result.user.isAdmin === false) {
        navigate('/get-app');
      }
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleMobileLogin = () => {
    setShowMobileModal(true);
  };

  const handleMobileLoginSuccess = (user) => {
    // Check if there's a redirect URL stored from DirectLinkAuth
    const redirectUrl = localStorage.getItem('redirectAfterAuth');
    if (redirectUrl) {
      console.log('AuthPage: Mobile login success, redirecting to stored URL:', redirectUrl);
      localStorage.removeItem('redirectAfterAuth'); // Clean up
      // Use hash-based navigation for hash router
      if (redirectUrl.startsWith('#')) {
        window.location.hash = redirectUrl.substring(1); // Remove the # and set hash
      } else {
        window.location.href = redirectUrl; // Fallback for full URLs
      }
      return;
    }
    
    // The modal will handle storing the token and user data
    // The auth context should pick up the changes
    window.location.reload(); // Simple approach to trigger auth context refresh
  };

  return (
    <div className="login-container">
      {/* Header with logo and sign up link */}
      <div className="login-header">
        <div className="logo-section">
          <img 
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets//Yesodsybol%20(1).jpeg" 
            alt="Yesod Logo" 
            className="header-logo"
          />
        </div>
        <div className="signup-section">
          <span className="signup-text">
            Don't have an admin account? <Link to="/signup" className="signup-link">Sign up</Link>
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="login-content">
        <div className="login-form-container">
          <h1 className="login-title">
            Community Login
          </h1>
          
          {error && (
            <div className="login-error" style={{
              padding: '16px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#c33',
              fontSize: '14px',
              lineHeight: '1.5',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}
          
          {awaitingEmailConfirmation ? (
            <form onSubmit={handleVerifyOtp} className="login-form">
              <div className="form-field">
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                  Enter 6-digit code from your email
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  style={{ 
                    fontSize: '24px', 
                    letterSpacing: '8px', 
                    textAlign: 'center',
                    fontFamily: 'monospace'
                  }}
                  required
                  autoFocus
                />
              </div>
              
              <button 
                type="submit" 
                className="login-button"
                disabled={verifyingOtp || otpCode.length !== 6}
              >
                {verifyingOtp ? 'Verifying...' : 'Verify Email'}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setAwaitingEmailConfirmation(false);
                  setOtpCode('');
                  setError('');
                }}
                style={{
                  marginTop: '12px',
                  background: 'transparent',
                  border: 'none',
                  color: '#5669FF',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Back to login
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="login-form">
            <div className="form-field">
              <input
                type="email"
                className="form-input"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="form-field password-field">
              <input
                type={showPassword ? "text" : "password"}
                className="form-input"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
            
            <button 
              type="submit" 
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Continue'}
            </button>
          </form>
          )}
          
          <div className="divider">
            <span></span>
          </div>
          
          <div className="forgot-password">
            <Link to="/reset-password" className="forgot-link">
              Forgot password?
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Authentication Modal */}
      <MobileAuthModal
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        onSuccessfulLogin={handleMobileLoginSuccess}
      />
    </div>
  );
};

export default AuthPage;